import { Component, Input } from "@angular/core";
import { CellKeyDownEvent, GridOptions, RowClickedEvent } from "ag-grid-enterprise";

/** A key on the focused cell itself, not on a control inside a cell renderer. */
const isOnCellItself = (event: KeyboardEvent): boolean =>
  event.target instanceof Element && event.target.classList.contains("ag-cell");

@Component({
  selector: "app-ag-grid-table",
  templateUrl: "./ag-grid-table.component.html",
})
export class AgGridTableComponent {
  @Input() options!: GridOptions;
  /** @Sinan implement a type for the data prop */
  @Input() data!: any;
  @Input() hasHeader: boolean = false;
  @Input() hasBottomBorder: boolean = false;
  @Input() hasTopBorder: boolean = false;
  @Input() hasCursorPointer: boolean = true;
  @Input() customRowClass: string = "";
  @Input() customGridClass: string = "";
  @Input() selectedRowUsingGray: boolean = false;
  @Input() gridBgClass: string = "";
  @Input() hideGridBorders: boolean = false;

  private cachedGridOptions: GridOptions | undefined;
  private cachedOptionsRef: GridOptions | undefined;
  private cachedWrapperConfigKey = "";

  get gridOptions(): GridOptions {
    const wrapperConfigKey = [
      this.hasHeader,
      this.hasBottomBorder,
      this.hasTopBorder,
      this.hasCursorPointer,
      this.customRowClass,
    ].join("|");

    if (
      this.cachedGridOptions &&
      this.cachedOptionsRef === this.options &&
      this.cachedWrapperConfigKey === wrapperConfigKey
    ) {
      return this.cachedGridOptions;
    }

    this.cachedOptionsRef = this.options;
    this.cachedWrapperConfigKey = wrapperConfigKey;
    const activatesFromKeyboard = !this.options?.onCellKeyDown && !!this.options?.onRowClicked;
    this.cachedGridOptions = {
      ...this.options,
      headerHeight: this.hasHeader ? undefined : 0,
      // Keyboard access for tables whose cells take focus (suppressCellFocus: false): the hidden
      // header isn't a Tab stop, and Enter/Space on a focused row does what clicking it does.
      // A table's own settings win.
      suppressHeaderFocus: this.options?.suppressHeaderFocus ?? !this.hasHeader,
      onCellKeyDown:
        this.options?.onCellKeyDown ?? ((params) => this.activateRowFromKeyboard(params)),
      // One Tab stop for the whole grid (#6246, the ARIA grid pattern): Tab and Shift+Tab leave it
      // from any row, and the arrow keys move between rows. ag-grid's default Tab went on to the
      // next row's cell, so a 150-row list took 150 presses to get past. false hands Tab to the
      // browser. Only for tables that opt their cells into focus explicitly (suppressCellFocus:
      // false), which hold no tabbable controls: where cells do (the recurring detector's move
      // buttons), the browser's Tab would visit them in DOM order, which ag-grid doesn't keep in
      // step with a sorted grid, and a visible header still catches Shift+Tab from the first row.
      tabToNextCell:
        this.options?.tabToNextCell ??
        (this.options?.suppressCellFocus === false ? () => false : undefined),
      // With rowSelection set, the grid's own Space handling toggles the row's selection before
      // cellKeyDown fires, so Space on a selected row deselected it and onRowClicked reselected
      // it (two selectionChanged events). Space on the cell is ours when we activate the row.
      defaultColDef: activatesFromKeyboard
        ? {
            suppressKeyboardEvent: (params) =>
              params.event.key === " " && isOnCellItself(params.event),
            ...this.options?.defaultColDef,
          }
        : this.options?.defaultColDef,
      getRowStyle: (params) => {
        const baseStyles = this.options.getRowStyle ? this.options.getRowStyle(params) : {};
        const wrapperStyles = this.hasBottomBorder ? { border: "none" } : {};
        return { ...baseStyles, ...wrapperStyles };
      },
      getRowClass: (params) => {
        const classes = [];

        if (this.options.getRowClass) {
          const originalClass = this.options.getRowClass(params);
          if (originalClass) {
            classes.push(originalClass);
          }
        }

        if (this.hasCursorPointer) {
          classes.push("tw-cursor-pointer");
        }
        if (this.customRowClass) {
          classes.push(this.customRowClass);
        }
        return classes.join(" ");
      },
    };

    return this.cachedGridOptions;
  }

  private activateRowFromKeyboard(params: Pick<CellKeyDownEvent, "event" | "data" | "node">) {
    const event = params.event as KeyboardEvent | undefined;
    if (!this.options?.onRowClicked || (event?.key !== "Enter" && event?.key !== " ")) {
      return;
    }
    // Only for a key on the focused cell itself. The grid raises cellKeyDown for keys anywhere
    // inside a cell (even with suppressCellFocus: true), so Enter/Space on a control in a cell
    // renderer (e.g. the transaction table's "view savings" button) must keep its own action.
    if (!isOnCellItself(event)) {
      return;
    }
    event.preventDefault();
    // Like appKeyboardActivatable: a held key opens the row once, not on every OS repeat tick.
    if (event.repeat) {
      return;
    }
    // Every onRowClicked here reads only node/data, which a key-down event carries too.
    this.options.onRowClicked(params as unknown as RowClickedEvent);
  }
}
